import { useEffect, useMemo, useState } from 'react';
import { Search, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button, Input, Card, CardContent, Dialog } from '../../ui';
import { getProveedores, createProveedor, updateProveedor, deleteProveedor } from '../api';
import { handleAuthError } from '../../../../funciones/authHelper';

export default function Proveedores() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState('');
  const [drawer, setDrawer] = useState(null);   // { id?, nombre }
  const [saving, setSaving] = useState(false);
  const [errForm, setErrForm] = useState('');
  const [delItem, setDelItem] = useState(null);
  const [errDel, setErrDel] = useState('');

  const cargar = async () => {
    setLoading(true);
    try { const { data } = await getProveedores(); setItems(data); }
    catch (e) { if (handleAuthError) handleAuthError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? items.filter((p) => p.nombre.toLowerCase().includes(t)) : items;
  }, [items, q]);

  const abrirNuevo = () => { setErrForm(''); setDrawer({ nombre: '' }); };
  const abrirEdit  = (p) => { setErrForm(''); setDrawer({ id: p.id, nombre: p.nombre }); };

  const guardar = async () => {
    if (!drawer.nombre.trim()) { setErrForm('El nombre es obligatorio.'); return; }
    setSaving(true); setErrForm('');
    try {
      if (drawer.id) await updateProveedor(drawer.id, drawer);
      else           await createProveedor(drawer);
      setDrawer(null); await cargar();
    } catch (e) {
      setErrForm(e?.response?.data?.error || 'No se pudo guardar el proveedor.');
      if (handleAuthError) handleAuthError(e);
    } finally { setSaving(false); }
  };

  const confirmarBorrado = async () => {
    setErrDel('');
    try { await deleteProveedor(delItem.id); setDelItem(null); await cargar(); }
    catch (e) {
      setErrDel(e?.response?.data?.error || 'No se pudo eliminar.');
      if (handleAuthError) handleAuthError(e);
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Proveedores</h1>
          <p className="text-sm text-slate-500 mt-1">
            Lista común a <span className="font-medium text-slate-700">Cursos SEPE</span> y <span className="font-medium text-slate-700">LAE Formación</span> · {items.length} proveedores
          </p>
        </div>
        <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1.5" /> Añadir proveedor</Button>
      </div>

      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="max-w-sm">
            <Input
              prefix={<Search className="w-4 h-4" />}
              suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
              placeholder="Buscar proveedor…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5 w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={2} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>)}
              {!loading && filtrados.length === 0 && (
                <tr><td colSpan={2} className="px-4 py-10 text-center text-slate-400">
                  {q ? 'Sin resultados.' : 'Aún no hay proveedores. Añade el primero.'}
                </td></tr>
              )}
              {!loading && filtrados.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => abrirEdit(p)}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{p.nombre}</td>
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-slate-400 hover:text-slate-700 mr-2" title="Editar" onClick={() => abrirEdit(p)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="text-slate-400 hover:text-rose-500" title="Eliminar" onClick={() => { setErrDel(''); setDelItem(p); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Drawer alta/edición */}
      {drawer && (
        <>
          <div className="fixed inset-0 bg-black/25 z-40" onClick={() => setDrawer(null)} />
          <aside className="fixed top-0 right-0 h-full w-[480px] max-w-[90vw] bg-white shadow-2xl z-50 flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">{drawer.id ? 'Editar proveedor' : 'Nuevo proveedor'}</h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDrawer(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <Input
                  autoFocus
                  value={drawer.nombre}
                  onChange={(e) => setDrawer({ ...drawer, nombre: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
                  placeholder="Ej. P9 Formación"
                />
              </div>
              {errForm && <p className="text-sm text-rose-600">{errForm}</p>}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDrawer(null)}>Cancelar</Button>
              <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
            </div>
          </aside>
        </>
      )}

      <Dialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        title="Eliminar proveedor"
        description={delItem ? `¿Seguro que quieres eliminar "${delItem.nombre}"?` : ''}
        footer={
          <div className="flex flex-col items-stretch gap-2">
            {errDel && <p className="text-sm text-rose-600">{errDel}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDelItem(null)}>Cancelar</Button>
              <Button variant="danger" onClick={confirmarBorrado}>Eliminar</Button>
            </div>
          </div>
        }
      />
    </div>
  );
}
